import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface SpaceListItem {
  id: number
  name: string
  image_url: string | null
  location: string | null
  min_capacity: number
  max_capacity: number
  is_reservable: number
  is_visible: number
}

const PAGE_SIZE = 6

export default function SpaceList() {
  const navigate = useNavigate()
  const [spaces, setSpaces] = useState<SpaceListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  useEffect(() => {
    fetchSpaces()
  }, [page])

  const fetchSpaces = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const params = new URLSearchParams()
      params.append('is_visible', '1')
      params.append('skip', String((page - 1) * PAGE_SIZE))
      params.append('limit', String(PAGE_SIZE))

      const res = await fetch(getApiUrl(`/marubit/api/space/list?${params.toString()}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setSpaces(data.spaces)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const getPageNumbers = () => {
    const start = Math.max(1, page - 2)
    const end = Math.min(totalPages, start + 4)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300 text-[15px]">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">공간 예약 신청</h1>
          <p className="text-[14px] text-gray-400">총 {total}건</p>
          <p className="text-[14px] text-[#6b5248] font-medium">
            예약하고 싶은 공간을 클릭하여 신청하세요.
          </p>
        </div>

        {/* 목록 */}
        {spaces.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-300 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            등록된 공간이 없습니다.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {spaces.map(space => (
              <div
                key={space.id}
                onClick={() => space.is_reservable === 1 && navigate(`/space/${space.id}`)}
                className={[
                  'bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.08)] transition-all duration-200 flex flex-col',
                  space.is_reservable === 1
                    ? 'cursor-pointer hover:shadow-[0_6px_24px_rgba(136,114,103,0.18)] hover:-translate-y-1'
                    : 'cursor-default opacity-80',
                ].join(' ')}
              >
                {/* 이미지 */}
                <div className="w-full h-[200px] bg-[#ede8e4] overflow-hidden flex items-center justify-center relative">
                  {space.image_url ? (
                    <img
                      src={space.image_url}
                      alt={space.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-[#c4b5ad]">
                      <svg viewBox="0 0 24 24" width={36} height={36} fill="none" stroke="currentColor" strokeWidth={1.2}>
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span className="text-[12px]">이미지 없음</span>
                    </div>
                  )}
                </div>

                {/* 내용 */}
                <div className="flex flex-col flex-1 p-5">

                  {/* 상태 배지 */}
                  <div className="mb-3">
                    {space.is_reservable === 1 ? (
                      <span className="inline-block px-3 py-[3px] text-[12px] font-bold bg-[#887267] text-white rounded-full">
                        예약하기
                      </span>
                    ) : (
                      <span className="inline-block px-3 py-[3px] text-[12px] font-bold bg-gray-200 text-gray-400 rounded-full">
                        예약불가
                      </span>
                    )}
                  </div>

                  {/* 공간명 */}
                  <h2 className="text-[16px] font-bold text-[#3d2f2a] mb-1 leading-snug">
                    {space.name}
                  </h2>

                  {/* 구분선 */}
                  <div className="border-t border-[#f0ebe8] mt-auto pt-3 flex flex-col gap-[6px]">
                    {/* 위치 */}
                    {space.location && (
                      <div className="flex items-center gap-2 text-[12px] text-gray-400">
                        <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2}>
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <span>{space.location}</span>
                      </div>
                    )}

                    {/* 인원 */}
                    <div className="flex items-center gap-2 text-[12px] text-gray-400">
                      <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2}>
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      <span>
                        {space.min_capacity === space.max_capacity
                          ? `${space.max_capacity}명`
                          : `${space.min_capacity}~${space.max_capacity}명`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ‹
            </button>
            {getPageNumbers().map(num => (
              <button
                key={num}
                onClick={() => setPage(num)}
                className={[
                  'w-9 h-9 flex items-center justify-center rounded-lg text-[14px] font-medium transition-colors',
                  num === page
                    ? 'bg-[#887267] text-white border border-[#887267]'
                    : 'border border-gray-200 text-gray-500 hover:border-[#887267] hover:text-[#887267]',
                ].join(' ')}
              >
                {num}
              </button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ›
            </button>
          </div>
        )}

      </div>
    </div>
  )
}